import type {
	ActivityProjectionAxis,
	CompanyRadar,
	RadarAxis,
	Tier,
} from "@/types/api";

/**
 * The geometry and the wording of the radar, apart from the drawing.
 *
 * Extracted so it can be tested: a chart is the one place where a wrong number
 * still looks plausible, and "the shape is a bit off" is not something anyone
 * notices in review.
 *
 * An axis is measured in TIERS from the centre, not in points. Points grow
 * without limit and the busiest domain would swallow the chart; the ladder is
 * what makes two domains comparable. Spec: curoBackend/docs/LEARNER_ACTIVITY_RADAR.md.
 */

/** One axis, reduced to what the chart needs. */
export interface Spoke {
	key: string;
	label: string;
	/** Tiers from the centre. */
	position: number;
	/** Where it was at the comparison date, when there is one. */
	before: number | null;
	/** Where completing every planned task would put it. */
	potential: number | null;
	tier: Tier;
	points: number;
	pieces: number;
}

export interface Point {
	x: number;
	y: number;
}

/** Wrap a domain label at words so long names stay inside the radar SVG. */
export function radarLabelLines(label: string, maxCharacters = 12): string[] {
	const words = label.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return [""];
	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		const next = current ? `${current} ${word}` : word;
		if (current && next.length > maxCharacters) {
			lines.push(current);
			current = word;
		} else {
			current = next;
		}
	}
	if (current) lines.push(current);
	return lines;
}

function pointText(point: Point): string {
	return `${point.x.toFixed(1)},${point.y.toFixed(1)}`;
}

function distance(a: Point, b: Point): number {
	return Math.hypot(b.x - a.x, b.y - a.y);
}

function towards(from: Point, to: Point, amount: number): Point {
	const length = distance(from, to);
	if (length === 0) return from;
	const ratio = amount / length;
	return {
		x: from.x + (to.x - from.x) * ratio,
		y: from.y + (to.y - from.y) * ratio,
	};
}

interface RoundedCorner {
	point: Point;
	start: Point;
	end: Point;
}

function roundedCorner(
	points: readonly Point[],
	index: number,
	cornerRadius: number,
): RoundedCorner {
	const point = points[index] ?? points[0] ?? { x: 0, y: 0 };
	const previous = points[(index - 1 + points.length) % points.length] ?? point;
	const next = points[(index + 1) % points.length] ?? point;
	const trim = Math.min(
		Math.max(0, cornerRadius),
		distance(point, previous) / 2,
		distance(point, next) / 2,
	);
	return {
		point,
		start: towards(point, previous, trim),
		end: towards(point, next, trim),
	};
}

/**
 * Where one spoke's vertex sits.
 *
 * Angles start at twelve o'clock and run clockwise, so the first axis is at the
 * top where a reader looks first. A position is clamped just inside the outer
 * ring: the ring above the busiest domain is the room left to climb, and a
 * vertex drawn on top of it would say there is none.
 */
export function vertex(
	index: number,
	count: number,
	position: number,
	rings: number,
	radius: number,
): Point {
	const angle = (Math.PI * 2 * index) / Math.max(1, count) - Math.PI / 2;
	const safeRings = Math.max(1, rings);
	const reach =
		(Math.max(0, Math.min(position, safeRings - 0.02)) / safeRings) * radius;
	return { x: reach * Math.cos(angle), y: reach * Math.sin(angle) };
}

/** The polygon through every spoke, as an SVG points list. */
export function polygon(
	spokes: readonly Spoke[],
	value: (spoke: Spoke) => number | null,
	rings: number,
	radius: number,
	centre: Point = { x: 0, y: 0 },
): string {
	return spokes
		.map((spoke, i) => {
			const point = vertex(i, spokes.length, value(spoke) ?? 0, rings, radius);
			return `${(centre.x + point.x).toFixed(1)},${(centre.y + point.y).toFixed(1)}`;
		})
		.join(" ");
}

/**
 * A closed SVG path with genuinely rounded corners.
 *
 * `strokeLinejoin="round"` only rounds the outside of a thick stroke; it does
 * not change the silhouette of the fill. This trims each corner by the given
 * amount and joins the two neighbouring edges with a quadratic curve. The trim
 * is capped at half of either edge, so tight or uneven radar shapes cannot fold
 * over themselves.
 */
export function roundedPolygonPath(
	points: readonly Point[],
	cornerRadius: number,
): string {
	if (points.length === 0) return "";
	if (points.length < 3 || cornerRadius <= 0) {
		return `${points
			.map((point, index) => `${index === 0 ? "M" : "L"} ${pointText(point)}`)
			.join(" ")} Z`;
	}

	const corners = points.map((_, index) =>
		roundedCorner(points, index, cornerRadius),
	);

	const first = corners[0];
	if (!first) return "";

	return [
		`M ${pointText(first.start)}`,
		...corners.flatMap((corner, index) => [
			`Q ${pointText(corner.point)} ${pointText(corner.end)}`,
			index < corners.length - 1
				? `L ${pointText(corners[index + 1]?.start ?? first.start)}`
				: `L ${pointText(first.start)}`,
		]),
		"Z",
	].join(" ");
}

/** The midpoint of one rounded corner, exactly on the quadratic path. */
export function roundedPolygonMarkerPoint(
	points: readonly Point[],
	index: number,
	cornerRadius: number,
): Point {
	if (points.length < 3 || cornerRadius <= 0) {
		return points[index] ?? points[0] ?? { x: 0, y: 0 };
	}

	const corner = roundedCorner(points, index, cornerRadius);
	return {
		x: (corner.start.x + 2 * corner.point.x + corner.end.x) / 4,
		y: (corner.start.y + 2 * corner.point.y + corner.end.y) / 4,
	};
}

/** "Tier 3 · 41%" — the tier, and how far through it. */
export function tierLabel(tier: Tier): string {
	return `Tier ${tier.level} · ${Math.floor(Math.max(0, Math.min(1, tier.progress)) * 100)}%`;
}

/** "1.2 to Tier 4" — what is left of the current tier, in points. */
export function toNextLabel(tier: Tier, points: number): string {
	const left = Math.max(0, tier.next - points);
	return `${left.toFixed(1)} to Tier ${tier.level + 1}`;
}

/** Which way an axis moved since the comparison date. */
export function movement(axis: Pick<RadarAxis, "points" | "tier">): {
	gained: number;
	tiersUp: number;
} {
	const before = axis.points.before ?? 0;
	const gained = Math.round((axis.points.now - before) * 10) / 10;
	const tiersUp = axis.tier.before
		? axis.tier.now.level - axis.tier.before.level
		: 0;
	return { gained, tiersUp };
}

/** The formats behind a domain, busiest first: "reading 12 · listening 3". */
export function formatSummary(byFormat: Record<string, number>): string {
	return (
		Object.entries(byFormat)
			.filter(([, n]) => n > 0)
			.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
			.map(([format, n]) => `${format} ${n}`)
			.join(" · ") || "—"
	);
}

/** A learner's axes as spokes, in the order the radar gave them. */
export function spokesFromAxes(axes: readonly RadarAxis[]): Spoke[] {
	return axes.map((axis) => ({
		key: axis.key,
		label: axis.domain,
		position: axis.position.now,
		before: axis.position.before,
		potential: null,
		tier: axis.tier.now,
		points: axis.points.now,
		pieces: axis.counts.pieces,
	}));
}

/** Current and lifetime-plan potential on one shared set of learner axes. */
export function projectionSpokes(
	projection: readonly ActivityProjectionAxis[],
	current: readonly RadarAxis[],
): Spoke[] {
	const currentByKey = new Map(current.map((axis) => [axis.key, axis]));
	return projection.map((axis) => {
		const observed = currentByKey.get(axis.key);
		return {
			key: axis.key,
			label: axis.domain,
			position: axis.position.now,
			before: observed?.position.before ?? null,
			potential: axis.position.potential,
			tier: axis.tier.now,
			points: axis.points.now,
			pieces: observed?.counts.pieces ?? 0,
		};
	});
}

/**
 * The company radar as spokes.
 *
 * A company axis carries the TOTAL its people earned and the AVERAGE per active
 * member; the shape is drawn from the average, which is what its tier is read
 * from - a bigger company should not look like a better one.
 */
export function companySpokes(company: CompanyRadar): Spoke[] {
	return company.axes.map((axis) => ({
		key: axis.key,
		label: axis.domain,
		position: axis.position,
		before: null,
		potential: null,
		tier: axis.tier,
		points: axis.average,
		pieces: axis.pieces,
	}));
}

/** The company shape and its all-planned-work counterfactual. */
export function companyProjectionSpokes(company: CompanyRadar): Spoke[] {
	return (company.projection?.axes ?? []).map((axis) => ({
		key: axis.key,
		label: axis.domain,
		position: axis.position.now,
		before: null,
		potential: axis.position.potential,
		tier: axis.tier.now,
		points: axis.average.now,
		pieces: 0,
	}));
}
